import type { GA4Item } from "@/platform/observability/analytics/v2/types/google"

import type { MetaCustomData, MetaMappedEvent } from "./types"

interface ItemParams {
  currency?: string
  value?: number
  items?: GA4Item[]
}

function buildProductCustomData(params: ItemParams): MetaCustomData {
  const items = params.items ?? []
  const contents = items.map((item) => ({
    id: item.item_id,
    quantity: item.quantity || 1,
    item_price: item.price,
  }))

  return {
    value: params.value,
    currency: params.currency,
    content_ids: contents.map((item) => item.id),
    content_type: "product",
    contents,
    num_items: contents.reduce((sum, item) => sum + item.quantity, 0) || undefined,
    content_name: items[0]?.item_name,
  }
}

function stringParam(params: Record<string, unknown>, key: string): string | undefined {
  const value = params[key]
  return typeof value === "string" && value.length > 0 ? value : undefined
}

function numberParam(params: Record<string, unknown>, key: string): number | undefined {
  const value = params[key]
  return typeof value === "number" ? value : undefined
}

export function buildMetaCustomData(event: MetaMappedEvent, params: Record<string, unknown>): MetaCustomData | undefined {
  switch (event) {
    case "search":
      return { search_string: stringParam(params, "search_term") }

    case "view_item":
    case "add_to_cart":
      return buildProductCustomData(params as ItemParams)

    case "sign_up":
      return {
        registration_method: stringParam(params, "method"),
        signup_channel: stringParam(params, "metaSignupChannel"),
      }

    case "loan_application_approved": {
      const userUuid = stringParam(params, "userUuid")
      if (!userUuid) return undefined
      return {
        value: numberParam(params, "credit_limit"),
        currency: stringParam(params, "currency"),
        order_id: userUuid,
      }
    }

    case "purchase":
      return {
        ...buildProductCustomData(params as ItemParams),
        order_id: stringParam(params, "transaction_id"),
      }
  }
}
